import { FC, ReactElement, useEffect } from 'react';

import { useParams } from 'react-router-dom';

import { OrganisationDetailCounselors } from '../../../containers';
import { scrollToTop } from '../../../helpers/scroll';
import useUnmount from '../../../hooks/useUnmount';
import { Counselor, CounselorFormData } from '../../../models/Counselor/Counselor';
import { defaultCounselorFilterValues, defaultCounselorOverview } from '../../../models/CounselorOverview/CounselorOverview';
import {
    archiveCounselor,
    createCounselor,
    fetchCounselorCandidates,
    finishCreateCounselor,
    updateCounselor,
} from '../../../redux/counselor/counselorActions';
import { setActiveFilterValues, setCounselorOverview } from '../../../redux/counselorOverview/counselorOverview';
import { fetchCounselorsForOrganisation } from '../../../redux/counselorOverview/counselorOverviewActions';
import { useTypedDispatch, useTypedSelector } from '../../../redux/store';

interface ConnectedOrganisationDetailCounselorsProps {
    className?: string;
}

const ConnectedOrganisationDetailCounselors: FC<ConnectedOrganisationDetailCounselorsProps> = ({
    className = '',
}): ReactElement => {
    const { organisationUuid = '' } = useParams();

    const dispatch = useTypedDispatch();

    const organisationIsLoading = useTypedSelector(state => state.organisationReducer.isLoading);
    const organisation = useTypedSelector(state => state.organisationReducer.organisation);

    const counselorIsLoading = useTypedSelector(state => state.counselorReducer.isLoading);
    const counselorIsUpdating = useTypedSelector(state => state.counselorReducer.isUpdating);
    const counselorIsSuccessful = useTypedSelector(state => state.counselorReducer.isSuccessful);
    const counselorIsSuccessfullyArchived = useTypedSelector(state => state.counselorReducer.isSuccessfullyArchived);
    const counselorHasActiveCandidates = useTypedSelector(state => state.counselorReducer.hasActiveCandidates);
    const counselorError = useTypedSelector(state => state.counselorReducer.error);

    const counselorOverviewIsLoading = useTypedSelector(state => state.counselorOverviewReducer.isLoading);
    const counselorOverviewError = useTypedSelector(state => state.counselorOverviewReducer.error);
    const counselorOverview = useTypedSelector(state => state.counselorOverviewReducer.counselorOverview);
    const activeFilterValues = useTypedSelector(state => state.counselorOverviewReducer.activeFilterValues);
    const pagination = useTypedSelector(state => state.counselorOverviewReducer.pagination);

    const userIsLoading = useTypedSelector(state => state.userReducer.isLoading);
    const user = useTypedSelector(state => state.userReducer.user);

    const isLoading = [
        organisationIsLoading,
        counselorOverviewIsLoading,
        counselorIsLoading,
        userIsLoading,
    ].some(Boolean);

    const handleAddCounselor = (formData: CounselorFormData): void => {
        if (organisation) dispatch(createCounselor(formData, organisation.id));
    };

    const handleEditCounselor = (formData: CounselorFormData): void => {
        if (organisation) dispatch(updateCounselor(formData, organisation.id));
    };

    const handleOpenArchiveDialog = (counselor: Counselor): void => {
        dispatch(fetchCounselorCandidates(organisationUuid, counselor.id));
    };

    const handleArchiveCounselor = (counselor: Counselor): void => {
        if (organisationUuid) dispatch(archiveCounselor(counselor, organisationUuid));
    };

    const handleCloseInviteCounselorSuccessMessage = (): void => {
        if (counselorIsSuccessful) {
            dispatch(finishCreateCounselor());
        }
    };

    const handlePaginationChange = (pageNumber: number): void => {
        const newFilterValues = {
            ...activeFilterValues,
            pageNumber,
        };

        dispatch(setActiveFilterValues(newFilterValues));

        scrollToTop();
    };

    useUnmount((): void => {
        dispatch(setCounselorOverview(defaultCounselorOverview()));
        dispatch(setActiveFilterValues(defaultCounselorFilterValues()));
    });

    useEffect((): void => {
        const isSuccessful = counselorIsSuccessful || counselorIsSuccessfullyArchived;

        if (isSuccessful && organisation) {
            dispatch(fetchCounselorsForOrganisation(organisation.id));
        }
    }, [counselorIsSuccessful, counselorIsSuccessfullyArchived]);

    useEffect((): void => {
        if (organisation) dispatch(fetchCounselorsForOrganisation(organisation.id));
    }, [activeFilterValues, organisation]);

    return (
        <OrganisationDetailCounselors
            isLoading={isLoading}
            counselorIsLoading={counselorIsLoading}
            counselorIsUpdating={counselorIsUpdating}
            counselorIsSuccessful={counselorIsSuccessful}
            counselorIsSuccessfullyArchived={counselorIsSuccessfullyArchived}
            counselorHasActiveCandidates={counselorHasActiveCandidates}
            counselorError={counselorError}
            counselorOverviewError={counselorOverviewError}
            counselors={counselorOverview.counselors}
            totalAmount={counselorOverview.totalAmount}
            pagination={pagination}
            userRoles={user?.roles}
            onAddCounselor={handleAddCounselor}
            onEditCounselor={handleEditCounselor}
            onOpenArchiveDialog={handleOpenArchiveDialog}
            onArchiveCounselor={handleArchiveCounselor}
            onCloseInviteCounselorSuccessMessage={handleCloseInviteCounselorSuccessMessage}
            onPaginationChange={handlePaginationChange}
            className={className}
        />
    );
};

export default ConnectedOrganisationDetailCounselors;
